import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import ProductLayout from './ProductLayout'
import ProductConfigurator from './ProductConfigurator'
import SEO from '../../components/SEO'

const API = ''

function getProductThumb(p) {
    if (p.primary_image) return `${API}/products/images/product/${p.primary_image}`
    if (p.image_url) return `${API}/products/images/${p.image_url.replace(/^var\//, '')}`
    return null
}

function getSpecValue(p, slug) {
    if (p.lookup_values) {
        const matches = p.lookup_values.filter(v => v.slug === slug).map(v => v.value)
        if (matches.length > 0) return matches.join(', ')
    }
    // Fallbacks
    if (slug === 'line_size') return p.lineSize || ''
    if (slug === 'meop') return p.MEOP || ''
    if (slug === 'cryo' || slug === 'Cryogenic' || slug === 'service_type') return p.cryogenic || ''
    return ''
}

/** Service-temp values from lookup tables (slug Cryogenic / service_type / cryo) or legacy column. */
function getServiceTempValues(p) {
    const fromLookup =
        getSpecValue(p, 'Cryogenic') ||
        getSpecValue(p, 'service_type') ||
        getSpecValue(p, 'cryo')
    if (fromLookup) {
        return fromLookup.split(', ').map(s => s.trim()).filter(Boolean)
    }
    if (p.cryogenic && p.cryogenic !== 'No' && p.cryogenic !== 'False') {
        return ['Cryogenic']
    }
    return []
}

function isCryogenic(p) {
    return getServiceTempValues(p).some(v => {
        const s = String(v).toLowerCase()
        return s.includes('cryo') || s === 'true' || s === 'yes'
    })
}

function isAmbient(p) {
    return getServiceTempValues(p).some(v => String(v).toLowerCase().includes('ambient'))
}

function getProductChips(p, categoryName) {
    if (!categoryName) return []
    const nameLower = categoryName.toLowerCase()
    const chips = []

    // 1. Temperature rating — only when the product actually has a value
    if (isCryogenic(p)) {
        chips.push({ text: 'Cryogenic', typeClass: 'ac-c' }) // blue
    }
    if (isAmbient(p)) {
        chips.push({ text: 'Ambient', typeClass: 'ac-g' }) // grey
    }

    // 2. Application (Flight / Ground)
    const app = getSpecValue(p, 'application')
    if (app) {
        if (app.toLowerCase().includes('flight')) {
            chips.push({ text: app, typeClass: 'ac-f' }) // teal
        } else {
            chips.push({ text: app, typeClass: 'ac-g' }) // grey
        }
    }

    // 3. Category-specific specs
    if (nameLower.includes('check')) {
        const line = getSpecValue(p, 'line_size')
        if (line) chips.push({ text: `${line} Line`, typeClass: 'ac-a' }) // amber
        
        const crack = getSpecValue(p, 'crackingPressure')
        if (crack) chips.push({ text: `${crack} PSI Crack`, typeClass: 'ac-f' }) // teal
        
        const mini = getSpecValue(p, 'mini_check')
        if (mini && (mini.toLowerCase() === 'true' || mini.toLowerCase() === 'yes')) {
            chips.push({ text: 'Mini Check', typeClass: 'ac-c' }) // blue
        }
    } else if (nameLower.includes('relief')) {
        const line = getSpecValue(p, 'line_size')
        if (line) chips.push({ text: `${line} Line`, typeClass: 'ac-a' }) // amber
        
        const rtype = getSpecValue(p, 'relief_type')
        if (rtype) chips.push({ text: rtype, typeClass: 'ac-c' }) // blue
        
        const minP = getSpecValue(p, 'min_relief_pressure')
        const maxP = getSpecValue(p, 'max_relief_pressure')
        if (minP || maxP) {
            const pText = minP && maxP ? `${minP}-${maxP}` : (minP || maxP)
            chips.push({ text: `${pText} PSI Relief`, typeClass: 'ac-f' }) // teal
        }
    } else if (nameLower.includes('regulator')) {
        const rtype = getSpecValue(p, 'regulator_type')
        if (rtype) chips.push({ text: `${rtype}`, typeClass: 'ac-a' }) // amber
        
        const outlet = getSpecValue(p, 'outletPressureRange')
        if (outlet) chips.push({ text: `Out: ${outlet} PSI`, typeClass: 'ac-f' }) // teal
        
        const inlet = getSpecValue(p, 'max_inlet_pressure')
        if (inlet) chips.push({ text: `In: ${inlet} PSI`, typeClass: 'ac-c' }) // blue
        
        const cv = getSpecValue(p, 'flow_coefficient')
        if (cv) chips.push({ text: `Cv: ${cv}`, typeClass: 'ac-g' }) // grey
    } else if (nameLower.includes('ball')) {
        const line = getSpecValue(p, 'line_size')
        if (line) chips.push({ text: `${line} Line`, typeClass: 'ac-a' }) // amber

        const cv = getSpecValue(p, 'flow_coefficient')
        if (cv) chips.push({ text: `Cv: ${cv}`, typeClass: 'ac-f' }) // teal

        const ops = getSpecValue(p, 'operators')
        if (ops) {
            ops.split(', ').forEach(op => {
                chips.push({ text: op, typeClass: 'ac-c' }) // blue
            })
        }
    }

    // Connections chip (after category-specific chips)
    const end = getSpecValue(p, 'end_connections')
    const inlet = getSpecValue(p, 'inlet_connections')
    const outlet = getSpecValue(p, 'outlet_connections')
    const conn = end || (inlet && outlet ? `${inlet} / ${outlet}` : (inlet || outlet))
    if (conn) chips.push({ text: conn, typeClass: 'ac-g' })

    return chips
}

const ProductCategoryPage = () => {
    const { categoryId } = useParams()
    const [categories, setCategories] = useState([])
    const [products, setProducts] = useState([])
    const [allProducts, setAllProducts] = useState([])
    const [filteredProductIds, setFilteredProductIds] = useState(null) // null = no filter active
    const [sortBy, setSortBy] = useState('default')

    // Reset sort order when changing categories
    useEffect(() => {
        setSortBy('default')
    }, [categoryId])

    useEffect(() => {
        axios.get(`${API}/api/categories`).then(r => setCategories(r.data)).catch(() => { })
        axios.get(`${API}/api/products`).then(r => setAllProducts(r.data)).catch(() => { })
    }, [])

    useEffect(() => {
        if (categoryId) {
            if (categoryId === 'all') {
                axios.get(`${API}/api/products`).then(r => setProducts(r.data)).catch(() => { })
            } else {
                axios.get(`${API}/api/products?category_id=${categoryId}`).then(r => setProducts(r.data)).catch(() => { })
            }
        }
    }, [categoryId])

    const currentCategory = useMemo(() => {
        if (categoryId === 'all') {
            return { id: 'all', name: 'All Products' }
        }
        return categories.find(c => c.id === Number(categoryId))
    }, [categories, categoryId])

    // Build nav categories (add custom products as a virtual tab)
    const navCategories = useMemo(() => {
        const cats = [{ id: 'all', name: 'All Products', slug: 'all-products' }]
        categories.forEach(c => {
            cats.push({ ...c, slug: `cat-${c.id}` })
        })
        cats.push({ id: 'custom', name: 'Custom Products', slug: 'custom-products' })
        return cats
    }, [categories])

    // Compute stats from products in this category
    const stats = useMemo(() => {
        if (!products.length) return []
        const result = []
        result.push({ key: 'Products', value: String(products.length), sub: 'in this category' })

        const cryoCount = products.filter(p => isCryogenic(p) || (getSpecValue(p, 'min_operating_temp') && parseInt(getSpecValue(p, 'min_operating_temp')) < -50)).length
        if (cryoCount > 0) {
            result.push({ key: 'Cryogenic', value: String(cryoCount), sub: 'cryo-rated models' })
        }

        // Collect unique line sizes
        const sizes = new Set()
        products.forEach(p => {
            const sz = getSpecValue(p, 'line_size')
            if (sz) {
                sz.split(', ').forEach(s => sizes.add(s.trim()))
            }
        })
        if (sizes.size > 0) {
            result.push({ key: 'Sizes', value: String(sizes.size), sub: 'line size options' })
        }

        return result.slice(0, 4)
    }, [products])

    // Products to display - filtered by configurator selections and sorted by chosen order
    const displayedProducts = useMemo(() => {
        let items = filteredProductIds === null 
            ? [...products] 
            : products.filter(p => filteredProductIds.includes(p.id))

        if (sortBy === 'name-asc') {
            items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        } else if (sortBy === 'name-desc') {
            items.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
        } else if (sortBy === 'model-asc') {
            items.sort((a, b) => {
                const modelA = a.model || a.modelNumber || a.sku || ''
                const modelB = b.model || b.modelNumber || b.sku || ''
                return modelA.localeCompare(modelB)
            })
        }

        return items
    }, [products, filteredProductIds, sortBy])

    if (!currentCategory) {
        return (
            <ProductLayout>
                <div style={{ padding: '48px 32px', textAlign: 'center', color: 'var(--pfc-text3)' }}>
                    Loading...
                </div>
            </ProductLayout>
        )
    }

    const activeCategoryId = categoryId === 'all' ? 'all' : Number(categoryId)

    return (
        <ProductLayout>
            <SEO
                title={currentCategory.name}
                description="Precision-engineered components for aerospace and industrial applications."
            />
            {/* Hero */}
            <div className="pfc-hero">
                <div className="pfc-hero-top">
                    <div className="pfc-hero-top-inner">
                        <div className="pfc-hero-eye">PFC Product Catalog</div>
                        <div className="pfc-hero-title">{currentCategory.name}</div>
                        <div className="pfc-hero-sub">
                            Precision-engineered components for aerospace and industrial applications.
                        </div>
                        <div className="pfc-hero-tags">
                            {products.some(p => isCryogenic(p)) && <span className="pfc-htag pfc-htag-t">Cryogenic Capable</span>}
                        </div>
                    </div>
                </div>

            </div>

            {/* Category tabs — sticky under navbar */}
            <div className="pfc-page-tabs">
                {navCategories.map((cat, i) => (
                    <React.Fragment key={cat.id}>
                        {i > 0 && <span className="pfc-tab-sep">·</span>}
                        <Link
                            to={cat.slug === 'custom-products' ? '/products/custom' : cat.id === 'all' ? '/products/category/all' : `/products/category/${cat.id}`}
                            className={`pfc-page-tab${cat.id === activeCategoryId ? ' active' : ''}`}
                        >
                            {cat.name}
                        </Link>
                    </React.Fragment>
                ))}
            </div>

            {/* Two-column layout */}
            <div className="pfc-two-col">
                <div className="pfc-left-col">
                    {/* Product Cards */}
                    <div className="pfc-card">
                        <div className="pfc-card-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                            <div>
                                <div className="pfc-card-title">Available Products</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label htmlFor="pfc-product-sort" style={{ fontSize: '11px', fontWeight: 700, color: 'var(--v2-text2, #666)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--pfc-mono, monospace)', margin: 0 }}>Sort By:</label>
                                <select
                                    id="pfc-product-sort"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid var(--v2-border, #e0e0e0)',
                                        background: 'var(--v2-surface, #fff)',
                                        color: 'var(--v2-text, #333)',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        outline: 'none',
                                        fontFamily: 'var(--pfc-sans, sans-serif)',
                                        transition: 'border-color 0.2s ease'
                                    }}
                                >
                                    <option value="default">Default</option>
                                    <option value="name-asc">Name: A to Z</option>
                                    <option value="name-desc">Name: Z to A</option>
                                    <option value="model-asc">Model: A to Z</option>
                                </select>
                            </div>
                        </div>
                        <div className="pfc-card-body">
                            <div className="pfc-variants-grid">
                                {displayedProducts.map(p => (
                                    <Link to={`/products/${p.id}`} className="pfc-vcard" key={p.id}>
                                        {getProductThumb(p) && (
                                            <img
                                                src={getProductThumb(p)}
                                                alt={p.name}
                                                className="pfc-vcard-img"
                                                onError={e => { e.target.style.display = 'none' }}
                                            />
                                        )}
                                        <div className="vc-top">
                                            <div className="vc-model">{(p.model || p.modelNumber || p.sku || '-') + (p.name ? ' - ' + p.name : '')}</div>
                                        </div>
                                        <div className="vc-ft">
                                            {getSpecValue(p, 'meop') && (
                                                <span className="pfc-achip ac-f">{getSpecValue(p, 'meop')} MEOP</span>
                                            )}
                                            {getProductChips(p, currentCategory.name).map((chip, index) => (
                                                <span key={index} className={`pfc-achip ${chip.typeClass}`}>
                                                    {chip.text}
                                                </span>
                                            ))}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* CTA Row */}
                    <div className="pfc-cta-row">
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-p"><i className="ti ti-file-description"></i></div>
                            <div className="pfc-ctac-title">Request a datasheet</div>
                            <div className="pfc-ctac-sub">Model drawings and test data on request.</div>
                            <Link to="/contactus" className="pfc-ctac-btn cbp">Request ↗</Link>
                        </div>
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-t"><i className="ti ti-messages"></i></div>
                            <div className="pfc-ctac-title">Talk to an engineer</div>
                            <div className="pfc-ctac-sub">Match you to the right product.<br /><span style={{ whiteSpace: 'nowrap' }}>(916) 626-3029</span></div>
                            <Link to="/contactus" className="pfc-ctac-btn cbo">Contact us</Link>
                        </div>
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-a"><i className="ti ti-settings-2"></i></div>
                            <div className="pfc-ctac-title">Custom spec?</div>
                            <div className="pfc-ctac-sub">Low NRE modifications of standard designs.</div>
                            <Link to="/products/custom" className="pfc-ctac-btn cbo">Custom products</Link>
                        </div>
                    </div>
                </div>

                {/* Configurator Sidebar */}
                <ProductConfigurator
                    products={products}
                    categoryId={categoryId === 'all' ? 'all' : Number(categoryId)}
                    categoryName={currentCategory.name}
                    onFilter={setFilteredProductIds}
                />
            </div>
        </ProductLayout>
    )
}

export default ProductCategoryPage
